import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Trash2,
  MapPin, ExternalLink, Package, X, Calculator,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  DESIGN_COAT_PRODUCTS,
  PIGMENTS_PRODUCTS,
  TOP_COAT_PRODUCTS,
} from '@/components/proepoxy/productData';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import { packLabel } from '@/components/proepoxy/utils';
import PrioritySelector from '@/components/PrioritySelector';

export default function MetallicStep7({ state, priority, setPriority, currentStep, goToStep, navigate, onExit , voucher: voucherProp }) {
  const {
    primerAreas, setPrimerAreas, designCoatSelections, pigmentSelections, topCoatSelections,
    steps,
    addPrimerArea, removePrimerArea, updatePrimerArea, updateAreaPackQty,
    updateAreaField, updateAreaFloorPrepQty,
    initAreaTopCoat, updateAreaTopCoatFinish, updateAreaTopCoatPackQty,
    getAreaTopCoatSubtotal, getAreaCoverage, getAreaSubtotal,
    initializeDesignCoatForArea, updateDesignCoatProduct,
    updateDesignCoatPackQtyLegacy, updateCrystalUnitsLegacy,
    getDesignCoatSubtotal, getDesignCoatCoverage,
    selectTopCoatProduct, updateTopCoatFinishLegacy, updateTopCoatPackQtyLegacy,
    getTopCoatSubtotal, getTopCoatCoverage,
    addMetallicPowder, updateMetallicPowderQty,
    addPigmentPaste, removePigmentPaste, updatePigmentPastePackQty,
    addGlitter, removeGlitter, updateGlitterPackQty,
    addSpray, updateSprayQty, getPigmentsSubtotal,
    calculateTotal, getItemCount,
  } = state;
  const voucher = voucherProp || { id: null, value: 0 };
  const voucherAreaM2 = (primerAreas || []).reduce((a, x) => a + (parseFloat(x.m2) || 0), 0);
  const voucherOff = Math.min(voucher.value || 0, calculateTotal());
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-[#27292E] mb-2">Order Summary</h2>
                  <p className="text-gray-600 mb-6">
                    Review your selections below before completing your order.
                  </p>

                  {/* Per-Area Breakdown */}
                  {primerAreas.map((area, index) => {
                    const floorPrepTotal = (() => {
                      let total = 0;
                      if (area.needsSmoothingCompounds) {
                        FLOOR_PREP_PRODUCTS.smoothing_compounds.forEach(product => {
                          const qty = area.smoothingCompounds?.[product.id] || 0;
                          total += qty * product.price;
                        });
                      }
                      if (area.needsSubFloorPrimer) {
                        FLOOR_PREP_PRODUCTS.primers.forEach(product => {
                          const qty = area.subFloorPrimers?.[product.id] || 0;
                          total += qty * product.price;
                        });
                      }
                      return total;
                    })();

                    const primerTotal = area.needsPrimer ? getAreaSubtotal(area) : 0;
                    const designCoatTotal = getDesignCoatSubtotal(area.id);
                    const topCoatTotal = getAreaTopCoatSubtotal(area);
                    const areaTotal = floorPrepTotal + primerTotal + designCoatTotal + topCoatTotal;

                    return (
                      <div key={area.id} className="mb-6 last:mb-0">
                        <div className="p-4 bg-gray-50 rounded-xl">
                          {/* Area Header */}
                          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                            <div>
                              <h3 className="font-bold text-[#27292E] text-lg">{area.name}</h3>
                              <p className="text-sm text-gray-500">{area.m2}m²</p>
                            </div>
                            <span className="text-lg font-bold text-[#7A0B7A]">
                              £{areaTotal.toFixed(2)}
                            </span>
                          </div>

                          <div className="space-y-3 text-sm">
                            {/* Floor Prep */}
                            {floorPrepTotal > 0 && (
                              <div>
                                <h4 className="font-medium text-gray-700 mb-1">Floor Preparation</h4>
                                {area.needsSmoothingCompounds && FLOOR_PREP_PRODUCTS.smoothing_compounds.map(product => {
                                  const qty = area.smoothingCompounds?.[product.id] || 0;
                                  if (qty === 0) return null;
                                  return (
                                    <div key={product.id} className="flex justify-between py-0.5 text-gray-600">
                                      <span>{product.name} × {qty}</span>
                                      <span>£{(qty * product.price).toFixed(2)}</span>
                                    </div>
                                  );
                                })}
                                {area.needsSubFloorPrimer && FLOOR_PREP_PRODUCTS.primers.map(product => {
                                  const qty = area.subFloorPrimers?.[product.id] || 0;
                                  if (qty === 0) return null;
                                  return (
                                    <div key={product.id} className="flex justify-between py-0.5 text-gray-600">
                                      <span>{product.name} × {qty}</span>
                                      <span>£{(qty * product.price).toFixed(2)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Primer */}
                            {primerTotal > 0 && (
                              <div>
                                <h4 className="font-medium text-gray-700 mb-1">Primer</h4>
                                <div className="flex justify-between py-0.5 text-gray-600">
                                  <span>
                                    {EPOXY_PRIMERS[area.primerType].name} ({area.color})
                                  </span>
                                </div>
                                {area.packs.map(pack => (
                                  <div key={pack.size} className="flex justify-between py-0.5 text-gray-600 pl-2">
                                    <span>{pack.size}m² pack × {pack.quantity}</span>
                                    <span>£{(pack.price * pack.quantity).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Design Coat */}
                            {designCoatTotal > 0 && (
                              <div>
                                <h4 className="font-medium text-gray-700 mb-1">Design Coat</h4>
                                {designCoatSelections[area.id] && (
                                  <>
                                    <div className="flex justify-between py-0.5 text-gray-600">
                                      <span>
                                        {designCoatSelections[area.id].productType === 'comfort' 
                                          ? DESIGN_COAT_PRODUCTS.comfort.name 
                                          : DESIGN_COAT_PRODUCTS.crystal.name}
                                      </span>
                                    </div>
                                    {designCoatSelections[area.id].productType === 'comfort' && designCoatSelections[area.id].packs.map(pack => (
                                      <div key={pack.size} className="flex justify-between py-0.5 text-gray-600 pl-2">
                                        <span>{packLabel(pack)} × {pack.quantity}</span>
                                        <span>£{(pack.price * pack.quantity).toFixed(2)}</span>
                                      </div>
                                    ))}
                                    {designCoatSelections[area.id].productType === 'crystal' && (
                                      <div className="flex justify-between py-0.5 text-gray-600 pl-2">
                                        <span>{designCoatSelections[area.id].crystalUnits} units</span>
                                        <span>£{(designCoatSelections[area.id].crystalUnits * DESIGN_COAT_PRODUCTS.crystal.price).toFixed(2)}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}

                            {/* Top Coat */}
                            {topCoatTotal > 0 && (
                              <div>
                                <h4 className="font-medium text-gray-700 mb-1">Top Coat</h4>
                                <div className="flex justify-between py-0.5 text-gray-600">
                                  <span>
                                    {TOP_COAT_PRODUCTS[area.topCoatType].name} ({area.topCoatFinish})
                                  </span>
                                </div>
                                {(area.topCoatPacks || []).map(pack => (
                                  <div key={pack.size} className="flex justify-between py-0.5 text-gray-600 pl-2">
                                    <span>{pack.size}m² pack × {pack.quantity}</span>
                                    <span>£{(pack.price * pack.quantity).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Pigments & Effects (Global) */}
                  {getPigmentsSubtotal() > 0 && (
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                        <div>
                          <h3 className="font-bold text-[#27292E] text-lg">Pigments & Effects</h3>
                          <p className="text-sm text-gray-500">Applied across all areas</p>
                        </div>
                        <span className="text-lg font-bold text-[#7A0B7A]">
                          £{getPigmentsSubtotal().toFixed(2)}
                        </span>
                      </div>

                      <div className="space-y-3 text-sm">
                        {/* Metallic Powders */}
                        {pigmentSelections.metallicPowders.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-700 mb-1">Metallic Pigment Powders</h4>
                            {pigmentSelections.metallicPowders.map((powder, idx) => {
                              const sizeInfo = PIGMENTS_PRODUCTS.metallicPowder.sizes.find(s => s.size === powder.size);
                              return (
                                <div key={`${powder.color}-${powder.size}`} className="flex justify-between py-0.5 text-gray-600">
                                  <span>{powder.color} ({powder.size}) × {powder.quantity}</span>
                                  <span>£{((sizeInfo?.price || 0) * powder.quantity).toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Pigment Paste */}
                        {pigmentSelections.pigmentPaste.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-700 mb-1">Pigment Paste</h4>
                            {pigmentSelections.pigmentPaste.map((paste) => (
                              <div key={paste.color}>
                                <span className="text-gray-600">{paste.color}</span>
                                {paste.packs.map(pack => (
                                  <div key={pack.size} className="flex justify-between py-0.5 text-gray-600 pl-2">
                                    <span>{pack.size}m² pack × {pack.quantity}</span>
                                    <span>£{(pack.price * pack.quantity).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Glitter */}
                        {pigmentSelections.glitter.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-700 mb-1">Glitter</h4>
                            {pigmentSelections.glitter.map((glitter) => (
                              <div key={glitter.type}>
                                <span className="text-gray-600">{glitter.type}</span>
                                {glitter.packs.map(pack => (
                                  <div key={pack.size} className="flex justify-between py-0.5 text-gray-600 pl-2">
                                    <span>{pack.size}m² pack × {pack.quantity}</span>
                                    <span>£{(pack.price * pack.quantity).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Sprays */}
                        {(pigmentSelections.marbleSprays.length > 0 || pigmentSelections.metallicSprays.length > 0) && (
                          <div>
                            <h4 className="font-medium text-gray-700 mb-1">Sprays</h4>
                            {pigmentSelections.marbleSprays.map((spray) => (
                              <div key={`marble-${spray.color}`} className="flex justify-between py-0.5 text-gray-600">
                                <span>Marble Spray - {spray.color} × {spray.quantity}</span>
                                <span>£{(PIGMENTS_PRODUCTS.sprays.marbleSpray.price * spray.quantity).toFixed(2)}</span>
                              </div>
                            ))}
                            {pigmentSelections.metallicSprays.map((spray) => (
                              <div key={`metallic-${spray.color}`} className="flex justify-between py-0.5 text-gray-600">
                                <span>Metallic Spray - {spray.color} × {spray.quantity}</span>
                                <span>£{(PIGMENTS_PRODUCTS.sprays.metallicSpray.price * spray.quantity).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Grand Total */}
                <div className="bg-[#7A0B7A] text-white rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white/80 text-sm">Order Total</p>
                      <p className="text-xs text-white/60">{getItemCount()} item{getItemCount() !== 1 ? 's' : ''} • Excl. VAT</p>
                      {voucherOff > 0 && <p className="text-xs text-amber-300 font-semibold mt-1" data-testid="pe-voucher-line">Training voucher − £{voucherOff.toFixed(2)} · to pay £{(calculateTotal() - voucherOff).toFixed(2)}</p>}
                    </div>
                    <p className="text-3xl font-bold">
                      £{calculateTotal().toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Priority */}
                <PrioritySelector value={priority} onChange={setPriority} />

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => goToStep(6)}
                    variant="outline"
                    className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => {
                      const items = [];
                      primerAreas.forEach((area) => {
                        // Floor prep
                        if (area.needsSmoothingCompounds) {
                          FLOOR_PREP_PRODUCTS.smoothing_compounds.forEach((p) => {
                            const qty = area.smoothingCompounds?.[p.id] || 0;
                            if (qty > 0) items.push({ category: 'floor_prep', product_name: p.name, qty, unit_price: p.price, line_total: qty * p.price, area_name: area.name });
                          });
                        }
                        if (area.needsSubFloorPrimer) {
                          FLOOR_PREP_PRODUCTS.primers.forEach((p) => {
                            const qty = area.subFloorPrimers?.[p.id] || 0;
                            if (qty > 0) items.push({ category: 'floor_prep', product_name: p.name, qty, unit_price: p.price, line_total: qty * p.price, area_name: area.name });
                          });
                        }
                        // Primer
                        if (area.needsPrimer && area.primerType && area.packs?.length) {
                          area.packs.forEach((pk) => {
                            if (pk.quantity > 0) items.push({
                              category: 'primer',
                              product_name: `${EPOXY_PRIMERS[area.primerType].name} (${area.color})`,
                              pack_label: `${pk.size}m² pack`, qty: pk.quantity,
                              unit_price: pk.price, line_total: pk.price * pk.quantity, area_name: area.name,
                            });
                          });
                        }
                        // Design coat (per-area)
                        const dc = designCoatSelections[area.id];
                        if (dc) {
                          if (dc.productType === 'comfort' && dc.packs?.length) {
                            dc.packs.forEach((pk) => {
                              if (pk.quantity > 0) items.push({
                                category: 'design_coat', product_name: DESIGN_COAT_PRODUCTS.comfort.name,
                                pack_label: `${pk.size}m² pack`, qty: pk.quantity,
                                unit_price: pk.price, line_total: pk.price * pk.quantity, area_name: area.name,
                              });
                            });
                          } else if (dc.productType === 'crystal' && dc.crystalUnits > 0) {
                            items.push({
                              category: 'design_coat', product_name: DESIGN_COAT_PRODUCTS.crystal.name,
                              qty: dc.crystalUnits, unit_price: DESIGN_COAT_PRODUCTS.crystal.price,
                              line_total: dc.crystalUnits * DESIGN_COAT_PRODUCTS.crystal.price, area_name: area.name,
                            });
                          }
                        }
                        // Top coat (per-area)
                        if (area.topCoat?.packs?.length) {
                          area.topCoat.packs.forEach((pk) => {
                            if (pk.quantity > 0) items.push({
                              category: 'top_coat',
                              product_name: `${TOP_COAT_PRODUCTS[area.topCoat.productType]?.name || 'Top Coat'} (${area.topCoat.finish || ''})`,
                              pack_label: `${pk.size}m² pack`, qty: pk.quantity,
                              unit_price: pk.price, line_total: pk.price * pk.quantity, area_name: area.name,
                            });
                          });
                        }
                      });
                      // Project-wide pigments
                      pigmentSelections.metallicPowders?.forEach((pw) => {
                        const sizeInfo = PIGMENTS_PRODUCTS.metallicPowder.sizes.find((s) => s.size === pw.size);
                        if (pw.quantity > 0) items.push({
                          category: 'pigment', product_name: `Metallic Pigment Powder - ${pw.color}`, pack_label: pw.size,
                          qty: pw.quantity, unit_price: sizeInfo?.price || 0, line_total: pw.quantity * (sizeInfo?.price || 0),
                        });
                      });
                      pigmentSelections.pigmentPaste?.forEach((paste) => {
                        (paste.packs || []).forEach((pk) => {
                          if (pk.quantity > 0) items.push({
                            category: 'pigment', product_name: `Pigment Paste - ${paste.color}`, pack_label: `${pk.size}m² pack`,
                            qty: pk.quantity, unit_price: pk.price, line_total: pk.price * pk.quantity,
                          });
                        });
                      });
                      pigmentSelections.glitter?.forEach((g) => {
                        (g.packs || []).forEach((pk) => {
                          if (pk.quantity > 0) items.push({
                            category: 'pigment', product_name: g.type, pack_label: `${pk.size}m² pack`,
                            qty: pk.quantity, unit_price: pk.price, line_total: pk.price * pk.quantity,
                          });
                        });
                      });
                      pigmentSelections.marbleSprays?.forEach((s) => {
                        if (s.quantity > 0) items.push({
                          category: 'pigment', product_name: `Marble Spray - ${s.color}`,
                          qty: s.quantity, unit_price: PIGMENTS_PRODUCTS.sprays.marbleSpray.price,
                          line_total: s.quantity * PIGMENTS_PRODUCTS.sprays.marbleSpray.price,
                        });
                      });
                      pigmentSelections.metallicSprays?.forEach((s) => {
                        if (s.quantity > 0) items.push({
                          category: 'pigment', product_name: `Metallic Spray - ${s.color}`,
                          qty: s.quantity, unit_price: PIGMENTS_PRODUCTS.sprays.metallicSpray.price,
                          line_total: s.quantity * PIGMENTS_PRODUCTS.sprays.metallicSpray.price,
                        });
                      });

                      submitProEpoxyOrder({
                        system: 'metallic',
                        items,
                        total: calculateTotal(),
                        voucher_id: voucher.id || undefined, area_m2: voucherAreaM2,
                        priority,
                        selections: { primerAreas, designCoatSelections, pigmentSelections },
                      }, navigate);
                    }}
                    className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl"
                    data-testid="proepoxy-complete-order"
                  >
                    <Check className="w-5 h-5 mr-2" />
                    Complete Order
                  </Button>
                </div>
              </div>
  );
}
